import { useEffect, useMemo } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'

import { PERIODS_VALUES } from '@app/constants/filters'
import { dateFormat, QUERY_PARAMS_KEY } from '@app/constants'

import { getFormattedDate } from '@app/utils/date'

import { FILTERS_PARAMS_KEYS } from '@app/api/constants'

import { getDefaultDatePeriod, getDefaultWeekPeriod } from './utils'

import type { UseFilterPeriodParams, UseFilterPeriodReturn } from './types'
import type { LntRangeValue } from '@app/components/base/Form/Input/DatePicker/Range/types'

export const useFilterPeriod = ({
  filters,
  setFilters,
  onSearch,
  handleClearFilters,
  atBeforeParamKey,
  atAfterParamKey
}: UseFilterPeriodParams): UseFilterPeriodReturn => {
  const [selectedPeriodParam, setSelectedPeriodParam] = useQueryParam(QUERY_PARAMS_KEY.period, StringParam)
  const isWeekSelected = useMemo(() => selectedPeriodParam === PERIODS_VALUES.week, [selectedPeriodParam])

  const dateAtAfterParamKey = atAfterParamKey ?? FILTERS_PARAMS_KEYS.createdAtAfter
  const dateAtBeforeParamKey = atBeforeParamKey ?? FILTERS_PARAMS_KEYS.createdAtBefore

  const resetDefaultPeriod = (isWeekSelected: boolean): void => {
    const defaultWeekPeriod = getDefaultWeekPeriod(dateAtAfterParamKey, dateAtBeforeParamKey)
    const defaultDatePeriod = getDefaultDatePeriod(dateAtAfterParamKey, dateAtBeforeParamKey)

    const defaultPeriod = isWeekSelected ? defaultWeekPeriod : defaultDatePeriod

    setFilters(prev => ({
      ...prev,
      ...defaultPeriod
    }))
  }

  const setDefaultPeriod = (isWeekSelected: boolean): void => {
    const hasQueryDateKeys = !!filters[dateAtAfterParamKey] && !!filters[dateAtBeforeParamKey]
    const hasQueryData = isWeekSelected ? !!filters.week : hasQueryDateKeys

    if (hasQueryData) return

    resetDefaultPeriod(isWeekSelected)

    const defaultWeekPeriod = getDefaultWeekPeriod(dateAtAfterParamKey, dateAtBeforeParamKey)
    const defaultDatePeriod = getDefaultDatePeriod(dateAtAfterParamKey, dateAtBeforeParamKey)

    const defaultPeriod = isWeekSelected ? defaultWeekPeriod : defaultDatePeriod

    onSearch({
      ...filters,
      ...defaultPeriod
    })
  }

  const handlePeriodChange = (value: string): void => {
    const isWeekSelected = value === PERIODS_VALUES.week
    const newSelectedPeriod = isWeekSelected ? value : null

    setSelectedPeriodParam(newSelectedPeriod)

    resetDefaultPeriod(isWeekSelected)
  }

  const handleRangeDateChange = (values: LntRangeValue): void => {
    const [dateAtAfter = null, dateAtBefore = null] = values ?? []

    const dateFilters = {
      [dateAtAfterParamKey]: getFormattedDate(dateAtAfter, dateFormat.YYYY_MM_DD),
      [dateAtBeforeParamKey]: getFormattedDate(dateAtBefore, dateFormat.YYYY_MM_DD)
    }

    setFilters(prev => ({
      ...prev,
      ...dateFilters
    }))
  }

  const handleClear = (): void => {
    handleClearFilters()
    handlePeriodChange(PERIODS_VALUES.date)
  }

  useEffect(() => {
    setDefaultPeriod(isWeekSelected)
  }, [])

  return { isWeekSelected, selectedPeriodParam, handlePeriodChange, handleRangeDateChange, handleClear }
}
